import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function LogoBox({ logo, setLogo, decor, setDecor }) {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); } catch (e) { toast.error(formatError(e)); } setBusy(false); };

  const upload = (f) => run(async () => {
    const fd = new FormData(); fd.append("file", f);
    await api.post("/branding/logo", fd);
    const r = await api.get("/branding/logo", { responseType: "blob" });
    setLogo(URL.createObjectURL(r.data));
  }, "Logo uploaded");
  const remove = () => run(async () => { await api.delete("/branding/logo"); setLogo(null); }, "Logo removed");

  return (
    <div className="border rounded-md p-3 space-y-2" data-testid="logo-box">
      <p className="text-sm font-medium">Your logo</p>
      <div className="flex items-center gap-3">
        <div className="h-14 w-20 rounded border bg-white flex items-center justify-center overflow-hidden">
          {logo ? <img src={logo} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" /> : <span className="text-[10px] text-neutral-400">No logo</span>}
        </div>
        <div className="flex flex-col gap-1.5">
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { e.target.files[0] && upload(e.target.files[0]); e.target.value = ""; }} data-testid="logo-file-input" />
          <Button size="sm" variant="outline" onClick={() => file.current.click()} disabled={busy} data-testid="logo-upload-button">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {logo ? "Replace logo" : "Upload logo"}
          </Button>
          {logo && <Button size="sm" variant="ghost" className="text-destructive" onClick={remove} disabled={busy} data-testid="logo-remove-button"><X size={14} /> Remove</Button>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">PNG, JPG, WEBP or GIF under 2 MB. Add a "Logo" line to place it on the sticker.</p>
      {logo && decor && (
        <div className="space-y-2 pt-1 border-t">
          <label className="flex items-center gap-2 text-sm pt-2"><Checkbox checked={decor.logo_bg} onCheckedChange={(v) => setDecor({ ...decor, logo_bg: !!v })} data-testid="logo-bg-check" /> Fill the whole sticker background with my logo</label>
          {decor.logo_bg && <label className="text-xs flex items-center gap-2">Background strength
            <input type="range" min={0.05} max={1} step={0.05} value={decor.logo_bg_opacity} onChange={(e) => setDecor({ ...decor, logo_bg_opacity: Number(e.target.value) })} data-testid="logo-bg-opacity" /></label>}
        </div>
      )}
    </div>
  );
}
